/**
 * Structural types for the loosely-typed payloads the device widgets read.
 *
 * Three shapes the widgets consume off the hub are loose by design, so they used
 * to be spelled `any` at every read:
 *
 *   • a device-value ROW is the hub's `DeviceValue` plus the trait keys the options
 *     endpoint and the device serializer add (`enum_options`, `max_value`, `step`, …)
 *     — the hub model declares none of them, so they were read through `as any`;
 *   • a value-log POINT is what the logging endpoints return per bucket
 *     (`{ avg }` for an analog series, `{ delta }` for a counter series);
 *   • a device's `settings` / an integration's `config` BAG is free-form JSON whose
 *     keys differ per integration (KNX group addresses, MQTT topics, …).
 *
 * Every field a widget actually reads is declared below; anything else stays
 * `unknown` and has to be narrowed where it is read. Nothing here changes runtime
 * behaviour — these are the narrow stand-ins for the `any`s they replace.
 *
 * Card t_a81ee773 (no-explicit-any batch 3/5). The integration-settings half of the
 * batch carries its own copy of the three helpers at the end of this file — the two
 * directories were kept free of cross-imports on purpose.
 */
import type { DeviceValueWithReferences } from '@/types';
import { WASTE_TANK_CLASS_IDS, WATER_TANK_CLASS_IDS } from '../../utils/waterTankDevices';
import { readSetting, variantFromSetting } from '../../utils/drawingSetting';

/** One enum choice, as `/devices/values/:id/options` spells it. */
export interface EnumOptionLike {
  value: unknown;
  label: unknown;
}

/**
 * A device-value row as the widgets read it: the hub row plus the trait keys the
 * options endpoint (`enum_options`, bounds, step) and the protocol payloads
 * (`property_key`) add. An intersection, not an interface, on purpose: the hub row
 * stays assignable to it, so a widget can type a lookup with this and read the extra
 * keys without a cast.
 */
export type WidgetDeviceValue = DeviceValueWithReferences & {
  /** Enum choices of an Enum value, fetched from the options endpoint. */
  enum_options?: EnumOptionLike[];
  /** Older alias for `valueKey` that the Z-Wave / lock payloads still carry. */
  property_key?: string;
  /** Numeric bounds and step some traits carry (both spellings are in the wild). */
  min?: number;
  max?: number;
  min_value?: number;
  max_value?: number;
  step?: number;
};

/** One point of a value-log series as the logging endpoints return it. */
export interface ValueLogPoint {
  /** Analog logs bucket an average; counter logs a delta; both also carry a raw value. */
  avg?: unknown;
  delta?: unknown;
  value?: unknown;
  timestamp?: string | number;
  [key: string]: unknown;
}

/** A free-form config bag: `Device.settings`, `Integration.config`, widget config. */
export type HubConfigBag = Record<string, unknown>;

/**
 * Read a field off a loosely-typed payload (API rows, stored config bags) without
 * an `any` or a cast: the value comes back `unknown` and the caller narrows it.
 */
export function readField(source: unknown, key: string): unknown {
  if (typeof source !== 'object' || source === null) return undefined;
  return (source as Record<string, unknown>)[key];
}

/** Narrow a loosely-typed payload to a keyed bag, or `null` when it is not one. */
export function asBag(source: unknown): Record<string, unknown> | null {
  return typeof source === 'object' && source !== null ? (source as Record<string, unknown>) : null;
}

/**
 * The S3 key of a device's thumbnail, when the row carries one.
 *
 * The device serializer copies `thumbnailS3Key` onto every row (it is what the
 * presigned `thumbnailUrl` is resolved from), but `Device` in `@/types` declares only
 * the resolved `thumbnailUrl`. The widgets used to reach the key through
 * `(device as any).thumbnailS3Key`; this reads the same field without the cast, and
 * answers `undefined` for anything that is not a non-empty string.
 */
export function deviceThumbnailS3Key(device: unknown): string | undefined {
  const key = readField(device, 'thumbnailS3Key');
  return typeof key === 'string' && key.length > 0 ? key : undefined;
}

/**
 * A device's value rows as the widgets read them. Some endpoints answer with the
 * `values` alias instead of `deviceValues` (the serializer spells the same list two
 * ways) — this accepts either, and anything that is not a list comes back empty.
 */
export function deviceValuesOf(device: unknown): WidgetDeviceValue[] {
  const rows = readField(device, 'deviceValues') ?? readField(device, 'values');
  return Array.isArray(rows) ? (rows as WidgetDeviceValue[]) : [];
}

/** The finite number a payload value holds, or `null` for anything else. */
export function toFiniteNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Which cover graphic a tile draws (see `cover/CoverAnimation.vue`).
 *
 * The hub reports every shade as `deviceType: 'cover'`, so the kind has to be read
 * from the icon and the name together: a "Terrace Pergola Screen" carries the
 * `blinds` icon, and a "Master Bedroom Roof Window" carries `window`.
 *
 * A lamella roof is the venetian blind in a frame with only its two side rails;
 * an awning is the screen without a sash, carried by a cassette at the head; the
 * window is a roof window with a tilting sash. The pergola drawing is gone
 * (owner, 27.10.2026) — a device still named "Pergola" resolves to the lamella
 * roof, the nearest drawing that exists.
 */
export type CoverKind = 'roller' | 'venetian' | 'shutter' | 'screen' | 'awning' | 'window' | 'lamella';

/** The values `settings.cover_style` accepts — the drawing ids themselves. */
export const COVER_KIND_VALUES: readonly CoverKind[] = [
  'roller',
  'venetian',
  'shutter',
  'screen',
  'awning',
  'window',
  'lamella',
];

/**
 * Pick the cover graphic from the device's icon / type / control type / name.
 *
 * Order matters twice over: a pergola *screen* is still a screen (so the screen
 * hints are tested before the roof hints), and a "Roof Window" is a window (so
 * the lamella test only looks for its own words, never for "roof" alone).
 *
 * The setting decides first (`cover_style`). What follows is what a device saved
 * before that setting existed still carries: the removed "Cover type" row wrote
 * `roller_shades`, `blinds`, `blinds_closed`, `curtains`, `pergola`, `roofing`,
 * `umbrella` and `window` straight into the icon, so those tokens have to keep
 * resolving to the drawing the row promised — pinned in
 * `deviceWidgetTypes.test.ts`. The pergola's own drawing is gone (owner,
 * 27.10.2026), so its name and icon resolve to the lamella roof.
 */
export function coverKindOf(device: {
  icon?: string | null;
  deviceType?: string | null;
  controlType?: string | null;
  deviceName?: string | null;
  shortName?: string | null;
  /** `settings` — the hub's `cover_style` setting outranks the legacy icon. */
  settings?: unknown;
}): CoverKind {
  const fromSetting = variantFromSetting(device.settings, 'cover_style', COVER_KIND_VALUES);
  if (fromSetting) return fromSetting as CoverKind;

  const haystack = [device.icon, device.deviceType, device.controlType, device.shortName, device.deviceName]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/awning|marki[zs]|umbrella|parasol/.test(haystack)) return 'awning';
  if (/screen|sunshade|zip_?screen|curtain|zavesa/.test(haystack)) return 'screen';
  if (/louvre|louver|lamel|tilt|roofing|pergola|carport|canopy/.test(haystack)) return 'lamella';
  if (/shutter|rollladen|rolladen|rol+eta|blinds_closed|blinds_2_closed/.test(haystack)) return 'shutter';
  if (/blind|venetian|zaluzi/.test(haystack)) return 'venetian';
  if (/window|okno/.test(haystack)) return 'window';
  return 'roller';
}

/**
 * Which sensor illustration a binary-sensor tile draws (see
 * `binary-sensor/BinarySensorAnimation.vue`) — or `generic` for "no drawing, keep
 * the device's own Material glyph".
 *
 * The kind is a *setting*: `settings.binary_sensor_subtype` is what the device
 * editor's picker writes and what the hub records when the sensor is paired, and
 * it is read first — a pick has to outrank everything else, or the picker would
 * be inert on every sensor the hub had already named. The icons the removed
 * "Sensor type" row wrote, and then the name, decide only when the setting is
 * absent or `generic` (the hub's own "no better guess").
 *
 * Every subtype draws the figure of its own name; `generic` is deliberately left
 * out of the map, so a paired-but-unnamed sensor still draws what its icon and
 * name say.
 *
 * There is one presence drawing, and it is the motion room: the poses the picker
 * used to offer (Proximity, Standing, Seated, Sleeping) were folded into it, so a
 * sensor that still carries one of those subtypes or glyphs keeps a figure.
 */
export type BinarySensorKind =
  | 'motion'
  | 'door'
  | 'window'
  | 'roof_window'
  | 'contact'
  | 'smoke'
  | 'flame'
  | 'gas'
  | 'leak'
  | 'vibration'
  | 'tamper'
  | 'generic';

/**
 * The icons the removed "Sensor type" row wrote, each meaning exactly one
 * drawing. They still read as a pick for every device saved before the setting
 * existed — nothing writes them any more.
 *
 * Material Symbols has no gas detector glyph, so `gas_meter` and `propane_tank`
 * stand in for it; the presence glyphs (`person`, `chair`, `hotel`,
 * `sensor_occupied`) were the poses the picker dropped, so they now land on the
 * room the motion sensor draws.
 *
 * `circle_notifications` was the row's "Generic" choice: it means "keep the glyph
 * the tile already drew", so it still gets out of the drawings.
 */
const BINARY_SENSOR_ICON_KINDS: Record<string, BinarySensorKind> = {
  directions_walk: 'motion',
  motion_sensor_active: 'motion',
  motion_sensor_idle: 'motion',
  person: 'motion',
  accessibility_new: 'motion',
  chair: 'motion',
  hotel: 'motion',
  sensor_door: 'door',
  door_front: 'door',
  window: 'window',
  roofing: 'roof_window',
  sensors: 'contact',
  sensor_occupied: 'motion',
  detector_smoke: 'smoke',
  local_fire_department: 'flame',
  gas_meter: 'gas',
  propane_tank: 'gas',
  water_damage: 'leak',
  water_drop: 'leak',
  vibration: 'vibration',
  shield: 'tamper',
  circle_notifications: 'generic',
};

/** The hub's `binary_sensor_subtype` values, as the drawing each one means. */
export const BINARY_SENSOR_SUBTYPE_KINDS: Record<string, BinarySensorKind> = {
  motion: 'motion',
  door: 'door',
  window: 'window',
  roof_window: 'roof_window',
  contact: 'contact',
  smoke: 'smoke',
  flame: 'flame',
  gas: 'gas',
  leak: 'leak',
  vibration: 'vibration',
  tamper: 'tamper',
};

/**
 * The subtypes the picker no longer offers: the four presence poses (Proximity,
 * Standing, Seated, Sleeping) — the drawings are gone, so every one of them
 * resolves to the motion room — and `fire`, which is drawn as `flame` now. A
 * sensor saved with one of these must not fall back to "no drawing".
 */
const BINARY_SENSOR_LEGACY_SUBTYPES: Record<string, BinarySensorKind> = {
  proximity: 'motion',
  presence: 'motion',
  seated: 'motion',
  sleeping: 'motion',
  fire: 'flame',
};

/** Pick the sensor illustration: the chosen subtype, the icon, then the name. */
export function binarySensorKindOf(device: {
  icon?: string | null;
  deviceType?: string | null;
  controlType?: string | null;
  deviceName?: string | null;
  shortName?: string | null;
  /** `settings.binary_sensor_subtype` — the hub's own semantic type */
  subtype?: string | null;
  /** `settings` — read directly so every caller passes the same source. */
  settings?: unknown;
}): BinarySensorKind {
  const subtype = String(readSetting(device.settings, 'binary_sensor_subtype') ?? device.subtype ?? '')
    .trim()
    .toLowerCase();
  const fromSubtype = BINARY_SENSOR_SUBTYPE_KINDS[subtype] ?? BINARY_SENSOR_LEGACY_SUBTYPES[subtype];
  if (fromSubtype) return fromSubtype;

  const icon = (device.icon ?? '').trim().toLowerCase();
  const fromIcon = BINARY_SENSOR_ICON_KINDS[icon];
  if (fromIcon) return fromIcon;

  const haystack = [device.deviceName, device.shortName, device.deviceType, device.controlType, subtype]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  // Order matters: the hazard words are the specific ones ("gas boiler room" is a
  // gas alarm, not a leak), the openings are tested before anything that might
  // merely sit in a room named after them ("Bedroom Window" is a window), and the
  // presence words — including the poses, now that only the one room is drawn —
  // come last, after every object that could carry them in a room's name.
  if (/gas|plin|methane|propane/.test(haystack)) return 'gas';
  if (/fire|flame|heat|po[zž]ar|ogenj|plamen/.test(haystack)) return 'flame';
  if (/smoke|dim\b/.test(haystack)) return 'smoke';
  if (/leak|flood|moisture|water|uhajanje|voda|vlaga/.test(haystack)) return 'leak';
  // A roof window is a window, so it is tested first — "Velux in the bedroom"
  // must not fall through to the casement.
  if (/roof ?window|roof light|skylight|velux|stre[šs]n|podstre[šs]/.test(haystack)) return 'roof_window';
  if (/window|okno/.test(haystack)) return 'window';
  if (/door|vrata/.test(haystack)) return 'door';
  if (/contact|reed|magnet|kontakt/.test(haystack)) return 'contact';
  if (/vibration|shock|impact|tresenj/.test(haystack)) return 'vibration';
  if (/tamper|intrusion|poseg|vlom/.test(haystack)) return 'tamper';
  if (
    /motion|movement|walk|gibanje|presence|occupan|person|standing|prisotn|zaseden|stoje|proximity|near|bli[zž]in|sleep|postelj|spanj|mattress|\bbed\b|chair|seat|desk|stol|sede[zž]/.test(
      haystack,
    )
  ) {
    return 'motion';
  }
  return 'generic';
}

/**
 * Which sensor illustration a plain number sensor draws (see
 * `sensor/SensorAnimation.vue`) — or `generic` for "no drawing, keep the glyph".
 *
 * These devices have no semantic type in the hub: a `sensor` control is just
 * "some number". The setting (`reading_type`) decides first; after it the icon,
 * then the unit (a hard fact about the reading), then the name. The icons the
 * removed "Sensor type" row wrote are read in that same order — they rank a unit
 * only because nothing writes them any more, so a device saved with one still
 * draws what the row promised.
 *
 * Ambiguous readings are resolved by name: `%` is a battery, a humidity or a
 * level; `km/h` is wind on a station and a vehicle speed anywhere else.
 */
export type SensorKind =
  | 'battery'
  | 'temperature'
  | 'humidity'
  | 'pressure'
  | 'co2'
  | 'air'
  | 'illuminance'
  | 'power'
  | 'energy'
  | 'voltage'
  | 'current'
  | 'wind'
  | 'windDirection'
  | 'rain'
  | 'waterLevel'
  | 'signal'
  | 'noise'
  | 'distance'
  | 'speed'
  | 'flow'
  | 'generic';

/** The values `settings.reading_type` accepts — the drawing ids themselves. */
export const SENSOR_KIND_VALUES: readonly SensorKind[] = [
  'battery',
  'temperature',
  'humidity',
  'pressure',
  'co2',
  'air',
  'illuminance',
  'power',
  'energy',
  'voltage',
  'current',
  'wind',
  'windDirection',
  'rain',
  'waterLevel',
  'signal',
  'noise',
  'distance',
  'speed',
  'flow',
  'generic',
];

/**
 * The icons the removed "Sensor type" row wrote, each meaning exactly one
 * drawing. Material Symbols has no valve or anemometer glyph, so those use the
 * closest that exists — the row's labels promised these drawings, pinned in
 * `deviceWidgetTypes.test.ts`.
 */
const SENSOR_ICON_KINDS: Record<string, SensorKind> = {
  battery_full: 'battery',
  battery_charging_full: 'battery',
  battery_5_bar: 'battery',
  device_thermostat: 'temperature',
  water_drop: 'humidity',
  compress: 'pressure',
  co2: 'co2',
  air: 'air',
  light_mode: 'illuminance',
  bolt: 'power',
  electric_meter: 'energy',
  electric_bolt: 'voltage',
  cable: 'current',
  wind_power: 'wind',
  explore: 'windDirection',
  rainy: 'rain',
  waves: 'waterLevel',
  signal_cellular_alt: 'signal',
  volume_up: 'noise',
  straighten: 'distance',
  speed: 'speed',
  water: 'flow',
  sensors: 'generic',
};

/** Units that name exactly one reading, whatever the device is called. */
const SENSOR_UNIT_KINDS: Record<string, SensorKind> = {
  '°c': 'temperature',
  '°f': 'temperature',
  k: 'temperature',
  hpa: 'pressure',
  mbar: 'pressure',
  bar: 'pressure',
  lx: 'illuminance',
  lux: 'illuminance',
  w: 'power',
  kw: 'power',
  kwh: 'energy',
  wh: 'energy',
  v: 'voltage',
  a: 'current',
  'm³/h': 'flow',
  'm3/h': 'flow',
  'l/min': 'flow',
  'l/h': 'flow',
  dbm: 'signal',
  dba: 'noise',
  db: 'noise',
  bft: 'wind',
  'm/s': 'wind',
  mph: 'wind',
  'mm/h': 'rain',
};

/** Pick the numeric sensor illustration: the setting, the icon, the unit, the name. */
export function sensorKindOf(device: {
  icon?: string | null;
  deviceType?: string | null;
  controlType?: string | null;
  deviceName?: string | null;
  shortName?: string | null;
  /** `unit` of the reading the tile shows. */
  unit?: string | null;
  /** Every `valueKey` / value name the device reports, for multi-value sensors. */
  valueNames?: (string | null | undefined)[];
  /** `settings` — the hub's `reading_type` outranks every loose hint. */
  settings?: unknown;
}): SensorKind {
  const fromSetting = variantFromSetting(device.settings, 'reading_type', SENSOR_KIND_VALUES);
  if (fromSetting) return fromSetting as SensorKind;
  const icon = (device.icon ?? '').trim().toLowerCase();
  const fromIcon = SENSOR_ICON_KINDS[icon];
  if (fromIcon) return fromIcon;

  const unit = (device.unit ?? '').trim().toLowerCase();
  const fromUnit = SENSOR_UNIT_KINDS[unit];
  if (fromUnit) return fromUnit;

  const haystack = [
    device.deviceName,
    device.shortName,
    device.deviceType,
    device.controlType,
    ...(device.valueNames ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  // Order matters the same way it does for covers: the readings that share a
  // word are told apart by the word that is theirs alone, and `%` never decides
  // on its own — a percentage is a battery, a humidity or a level only once the
  // name has said which.
  if (/co2|carbon_?dioxide|carbon dioxide/.test(haystack)) return 'co2';
  if (/wind_?dir|direction|smer/.test(haystack)) return 'windDirection';
  if (/wind|anemometer|veter\b/.test(haystack)) return 'wind';
  if (/rain|precip|padavin|de[zž]\b/.test(haystack)) return 'rain';
  if (/battery|baterij|charge|accumulator|soc\b/.test(haystack)) return 'battery';
  if (/temperature|temp\b|temperatur/.test(haystack)) return 'temperature';
  if (/humidity|moisture|vlaga|vlaznost|vla[zž]nost/.test(haystack)) return 'humidity';
  if (/pressure|tlak|barometer/.test(haystack)) return 'pressure';
  if (/air_?quality|pm2|pm10|particulate|voc|kakovost/.test(haystack)) return 'air';
  if (/illumin|lux|brightness|osvetljen/.test(haystack)) return 'illuminance';
  if (/energy|kwh|electric_meter|counter|meter\b/.test(haystack)) return 'energy';
  if (/voltage|napetost/.test(haystack)) return 'voltage';
  if (/current|tok\b|amper/.test(haystack)) return 'current';
  if (/\bpower\b|watt|solar|inverter|mo[cč]/.test(haystack)) return 'power';
  if (/level|tank|cistern|gladina|nivo|water_?level/.test(haystack)) return 'waterLevel';
  if (/signal|rssi|wifi|coverage|jakost/.test(haystack)) return 'signal';
  if (/noise|sound|decibel|loudness|hrup/.test(haystack)) return 'noise';
  if (/distance|range|oddaljen|razdalj/.test(haystack)) return 'distance';
  if (/speed|velocity|rpm|hitrost/.test(haystack)) return 'speed';
  if (/flow|pretok/.test(haystack)) return 'flow';
  return 'generic';
}

/**
 * Which appliance drawing a switch-type tile shows (see
 * `switch/SwitchAnimation.vue`) — or `generic` for "no drawing, keep the glyph".
 *
 * Switches, sockets, relays and the appliances hung off them have no semantic
 * type in the hub either: a switch is "some contact". The setting
 * (`appliance_type`) decides first — the picker writes it, and picking a load
 * also fills `settings.device_name`, the hub's *Connected Load Name* ("Coffee
 * Machine", "Pump") — then the icon the removed row wrote, then that load name,
 * then the device's own name.
 *
 * The valve is the one fitting that names itself: the hub's `valve` class
 * carries `icon: "valve"`, which is what draws the quarter-turn valve — its
 * `power` row is `Open/Close`, so a live tile means an open valve.
 */
export type SwitchKind =
  | 'coffee'
  | 'washer'
  | 'dryer'
  | 'dishwasher'
  | 'fridge'
  | 'oven'
  | 'kettle'
  | 'dimmer'
  | 'ev'
  | 'vacuum'
  | 'mower'
  | 'pump'
  | 'valve'
  | 'siren'
  | 'bell'
  | 'socket'
  | 'generic';

/** The values `settings.appliance_type` accepts — the drawing ids themselves. */
export const SWITCH_KIND_VALUES: readonly SwitchKind[] = [
  'coffee',
  'washer',
  'dryer',
  'dishwasher',
  'fridge',
  'oven',
  'kettle',
  'dimmer',
  'ev',
  'vacuum',
  'mower',
  'pump',
  'valve',
  'siren',
  'bell',
  'socket',
  'generic',
];

/** Material Symbols the removed appliance row wrote, as the drawing each means.
 *
 * The hub's own class defaults are deliberately absent — a socket is created
 * carrying `power`, a relay `toggle_on`, a doorbell `smart_button` — exactly
 * because they say nothing about the load: mapping `power` to the plain socket
 * drawing would shadow the name of every socket in the house. */
const SWITCH_ICON_KINDS: Record<string, SwitchKind> = {
  coffee: 'coffee',
  coffee_maker: 'coffee',
  local_cafe: 'coffee',
  local_laundry_service: 'washer',
  dry_cleaning: 'dryer',
  dishwasher: 'dishwasher',
  countertops: 'dishwasher',
  kitchen: 'fridge',
  blender: 'kettle',
  microwave: 'oven',
  lightbulb: 'dimmer',
  lamp: 'dimmer',
  ev_station: 'ev',
  battery_charging_full: 'ev',
  outlet: 'socket',
  robot_vacuum: 'vacuum',
  notifications_active: 'siren',
  doorbell: 'bell',
  valve: 'valve',
};

/** Pick the appliance illustration: the setting, the icon, the load name, the name. */
export function switchKindOf(device: {
  icon?: string | null;
  deviceType?: string | null;
  controlType?: string | null;
  deviceName?: string | null;
  shortName?: string | null;
  /** `settings.device_name` — the socket class's Connected Load Name. */
  connectedLoadName?: string | null;
  /** `settings` — the hub's `appliance_type` outranks every loose hint. */
  settings?: unknown;
}): SwitchKind {
  const fromSetting = variantFromSetting(device.settings, 'appliance_type', SWITCH_KIND_VALUES);
  if (fromSetting) return fromSetting as SwitchKind;

  const icon = (device.icon ?? '').trim().toLowerCase();
  const fromIcon = SWITCH_ICON_KINDS[icon];
  if (fromIcon) return fromIcon;

  // The load name is the hub's own answer, so it outranks the device's name.
  const load = (device.connectedLoadName ?? '').trim().toLowerCase();
  const fromLoad = applianceKind(load);
  if (fromLoad) return fromLoad;

  const haystack = [device.deviceName, device.shortName, device.deviceType, device.controlType]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return applianceKind(haystack) ?? 'generic';
}

/**
 * Whether a device's tile should draw an appliance (`SwitchAnimation`) — the
 * same question as `switchKindOf` returning something other than `generic`.
 */
export function hasApplianceArt(device: Parameters<typeof switchKindOf>[0]): boolean {
  return switchKindOf(device) !== 'generic';
}

/**
 * Which lamp drawing the tile shows (see `light/LightAnimation.vue`).
 *
 * A lamp is not an appliance: what it looks like is where it hangs, so the
 * family is the fitting — a flush dome, a pendant, a chandelier, a suspended bar,
 * a ring, a ceiling panel, a recessed downlight, a track of spots, a wall sconce,
 * a table lamp, a floor lamp, an LED strip.
 *
 * The setting (`light_type`) is read first; after it the icon and the name, which
 * is what a device saved before the setting existed carries. The class-default
 * bulb glyph is deliberately absent from the icon map: every light was created
 * with it, so it says "a light" and must fall through to the name rather than pin
 * every lamp in the house to a ceiling dome.
 */
export type LightKind =
  | 'ceiling'
  | 'pendant'
  | 'chandelier'
  | 'linear'
  | 'circular'
  | 'panel'
  | 'downlight'
  | 'spot'
  | 'wall'
  | 'table'
  | 'floor'
  | 'strip';

/** The values `settings.light_type` accepts — the drawing ids themselves. */
export const LIGHT_KIND_VALUES: readonly LightKind[] = [
  'ceiling',
  'pendant',
  'chandelier',
  'linear',
  'circular',
  'panel',
  'downlight',
  'spot',
  'wall',
  'table',
  'floor',
  'strip',
];

/** The icons a light is re-kinded with, as the fitting each means. The classes'
 *  own defaults (`lightbulb`, `light_mode`) are absent on purpose: they say "a
 *  light", so they must fall through to the name instead of pinning every lamp
 *  in the house to one fitting. */
const LIGHT_ICON_KINDS: Record<string, LightKind> = {
  light: 'ceiling',
  dine_lamp: 'pendant',
  light_group: 'chandelier',
  lightstrip: 'strip',
  wall_lamp: 'wall',
  table_lamp: 'table',
  floor_lamp: 'floor',
};

function lightKindFromName(text: string): LightKind | null {
  if (!text) return null;
  if (/chandelier|luster|kristaln/.test(text)) return 'chandelier';
  if (/floor lamp|standing lamp|stoj[eč]a|stojna|talna/.test(text)) return 'floor';
  if (/table lamp|desk lamp|bedside|namizn/.test(text)) return 'table';
  if (/pendant|hanging|vise[čc]|obe[šs]en/.test(text)) return 'pendant';
  if (/sconce|wall|nastavek|stenska/.test(text)) return 'wall';
  if (/downlight|recessed|vgradn/.test(text)) return 'downlight';
  if (/spot|track|reflector|reflektor/.test(text)) return 'spot';
  if (/strip|trak|led trak/.test(text)) return 'strip';
  if (/linear|linearno/.test(text)) return 'linear';
  if (/circular|ring|halo|obro[čc]/.test(text)) return 'circular';
  if (/panel|plo[šs][čc]a/.test(text)) return 'panel';
  if (/ceiling|plafon|stropn|stropna/.test(text)) return 'ceiling';
  return null;
}

/**
 * The lamp a light tile draws, from its setting, its icon and its name.
 *
 * A light with nothing to go on is a ceiling light: it is the one fitting every
 * installation has.
 */
export function lightKindOf(device: {
  icon?: string | null;
  deviceType?: string | null;
  controlType?: string | null;
  deviceName?: string | null;
  shortName?: string | null;
  /** `settings` — where the picked fitting lives. */
  settings?: unknown;
}): LightKind {
  const fromSetting = variantFromSetting(device.settings, 'light_type', LIGHT_KIND_VALUES);
  if (fromSetting) return fromSetting as LightKind;

  const icon = (device.icon ?? '').trim().toLowerCase();
  const fromIcon = LIGHT_ICON_KINDS[icon];
  if (fromIcon) return fromIcon;

  const haystack = [device.deviceName, device.shortName, device.deviceType, device.controlType]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return lightKindFromName(haystack) ?? 'ceiling';
}

/** The fields a device spells its class and its control in. */
export interface PumpDeviceLike {
  deviceType?: string | null;
  controlType?: string | null;
  deviceControl?: string | null;
}

/**
 * Whether the device is the hub's own Pump class.
 *
 * The class reports as a motor — `controlType: "fan"` — so a pump and a fan are
 * the same widget; only the class id (or a legacy control spelled `pump`) says
 * which drawing belongs on it.
 */
export function isPumpDevice(device: PumpDeviceLike): boolean {
  return [device.deviceType, device.controlType, device.deviceControl].some(
    (value) =>
      String(value ?? '')
        .trim()
        .toLowerCase() === 'pump',
  );
}

/**
 * Which pump drawing the tile shows (see `pump/PumpAnimation.vue`).
 *
 * `circulator` is the hydronic loop's own pump, `surface` an inline/end-suction
 * one standing beside the water, `submersible` a can hanging in a well or pond,
 * `sump` a pump in a pit, `pool` a pool's strainer-and-motor set, `well` a slim
 * pump down a borehole, `sewage` a closed chamber with its grinder, and
 * `booster` a pump with its pressure vessel.
 *
 * The setting (`pump_type`) is read first; after it the icon and the name, which
 * is what a device saved before the setting existed carries. The class-default
 * `water_pump` is deliberately absent from the icon map: it is what every pump
 * was created with, so it says "a pump" and must fall through to the name rather
 * than pin every pump in the house to a circulator.
 */
export type PumpKind = 'circulator' | 'surface' | 'submersible' | 'sump' | 'pool' | 'well' | 'sewage' | 'booster';

/** The values `settings.pump_type` accepts — the drawing ids themselves. */
export const PUMP_KIND_VALUES: readonly PumpKind[] = [
  'circulator',
  'surface',
  'submersible',
  'sump',
  'pool',
  'well',
  'sewage',
  'booster',
];

/** The icons the removed "Pump type" row wrote, as the drawing each means. */
const PUMP_ICON_KINDS: Record<string, PumpKind> = {
  heat_pump: 'circulator',
  plumbing: 'surface',
  water_full: 'submersible',
  flood: 'sump',
  pool: 'pool',
  water: 'well',
  wc: 'sewage',
  faucet: 'booster',
};

/** The words a pump names its own kind with, most specific first. */
function pumpKindFromName(text: string): PumpKind | null {
  if (!text) return null;
  if (/pool|bazen|bazenska|filter[_ ]?pump|pump[_ ]?filter/.test(text)) return 'pool';
  if (/sewage|sewer|grinder|odplak|kanalizac|fecal|faeces|waste[_ ]?water/.test(text)) return 'sewage';
  if (/sump|drain|kalna|odto[cč]|cellar/.test(text)) return 'sump';
  if (/borehole|bore[_ ]?hole|globinsk|vrtin|\bwell\b/.test(text)) return 'well';
  if (/submers|potopn|pond|ribnik/.test(text)) return 'submersible';
  if (/booster|pressure[_ ]?pump|tla[cč]na|hydrophore/.test(text)) return 'booster';
  if (/inline|in-line|surface|end[_ ]?suction|povr[sš]insk|kanalska/.test(text)) return 'surface';
  return null;
}

/** Pick the pump illustration: the setting, the icon, the name, then a circulator. */
export function pumpKindOf(
  device: {
    icon?: string | null;
    deviceName?: string | null;
    shortName?: string | null;
    /** `settings` — the hub's `pump_type` outranks the legacy icon. */
    settings?: unknown;
  } & PumpDeviceLike,
): PumpKind {
  const fromSetting = variantFromSetting(device.settings, 'pump_type', PUMP_KIND_VALUES);
  if (fromSetting) return fromSetting as PumpKind;

  const icon = (device.icon ?? '').trim().toLowerCase();
  const fromIcon = PUMP_ICON_KINDS[icon];
  if (fromIcon) return fromIcon;

  const haystack = [device.deviceName, device.shortName, device.deviceType, device.controlType, device.deviceControl]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return pumpKindFromName(haystack) ?? 'circulator';
}

/**
 * Which fan drawing the tile shows (see `fan/FanAnimation.vue`).
 *
 * A fan and a pump are one widget — the hub's Pump reports `controlType: "fan"` —
 * so the fan's kind is what tells the two drawings apart, and then one fan from
 * another: `ceiling` a ceiling fan seen from below with its blade-sweep ring,
 * `pedestal` a stand fan on its tall pole, `table` a desk fan, `wall` one on a
 * wall bracket, `tower` the column with its outlet grille, `extractor` the
 * bathroom's square wall fan, `duct` an inline fan inside its duct, and `erv` the
 * heat-recovery unit a whole ventilation system is built around.
 *
 * Read order is the one every family uses: the `fan_style` setting, then the icon,
 * then the name, then `ceiling`. The class-default `air` (and the other bare
 * propellers a driver sets) stay out of the icon map on purpose: they say "a fan"
 * and must fall through to the name rather than pin every fan in the house to the
 * ceiling.
 */
export type FanKind = 'ceiling' | 'pedestal' | 'table' | 'wall' | 'tower' | 'extractor' | 'duct' | 'erv';

/** The values `settings.fan_style` accepts — the drawing ids themselves. */
export const FAN_KIND_VALUES: readonly FanKind[] = [
  'ceiling',
  'pedestal',
  'table',
  'wall',
  'tower',
  'extractor',
  'duct',
  'erv',
];

/** The icons that do name a mounting, as the drawing each means. */
const FAN_ICON_KINDS: Record<string, FanKind> = {
  ceiling_fan: 'ceiling',
  mode_fan: 'pedestal',
  air_purifier: 'erv',
  wall_art: 'wall',
  swap_horiz: 'duct',
  sync_alt: 'erv',
};

/** The words a fan names its own kind with, most specific first. */
function fanKindFromName(text: string): FanKind | null {
  if (!text) return null;
  // The ventilation system first: "prezračevanje" also contains no fan word, but
  // an MVHR's name usually carries both its ducts and its extraction.
  if (/\berv\b|\bhrv\b|mvhr|recuperat|rekuperat|prezra[cč]eval|heat[_ ]?recovery|ventilation[_ ]?unit/.test(text)) {
    return 'erv';
  }
  if (/extract|exhaust|odsesav|odvajal|bathroom|kopalni|\bwc\b|toilet/.test(text)) return 'extractor';
  if (/duct|kanalsk|in-line|inline|channel[_ ]?fan/.test(text)) return 'duct';
  if (/tower|stolpn|column[_ ]?fan/.test(text)) return 'tower';
  if (/ceiling|stropn|plafon|propeller|strop/.test(text)) return 'ceiling';
  if (/wall|stensk|bracket|konsol/.test(text)) return 'wall';
  if (/table|desk|namizn|pisaln/.test(text)) return 'table';
  if (/pedestal|stand|standing|stoje[cč]|floor[_ ]?fan/.test(text)) return 'pedestal';
  return null;
}

/** Pick the fan illustration: the setting, the icon, the name, then a ceiling fan. */
export function fanKindOf(
  device: {
    icon?: string | null;
    deviceName?: string | null;
    shortName?: string | null;
    /** `settings` — the hub's `fan_style` outranks every loose hint. */
    settings?: unknown;
  } & PumpDeviceLike,
): FanKind {
  const fromSetting = variantFromSetting(device.settings, 'fan_style', FAN_KIND_VALUES);
  if (fromSetting) return fromSetting as FanKind;

  const icon = (device.icon ?? '').trim().toLowerCase();
  const fromIcon = FAN_ICON_KINDS[icon];
  if (fromIcon) return fromIcon;

  const haystack = [device.deviceName, device.shortName, device.deviceType, device.controlType, device.deviceControl]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return fanKindFromName(haystack) ?? 'ceiling';
}

/**
 * Which tank drawing the tile shows (see `tank/TankAnimation.vue`).
 *
 * `ibc` is the 1000-litre tote in its steel cage, `cistern` a standing
 * cylindrical cistern with its lid and neck, `horizontal` a lying cistern on
 * saddles, `buffer` an insulated thermal store with its ports and thermometer,
 * `treatment` the biological treatment plant with its blower, `septic` the older
 * two-chamber vault, and `expansion` a heating expansion vessel.
 *
 * The first six are the shapes the 3D tank family already models (`ibc`,
 * `oval-vertical`, `oval-horizontal`, the buffer, the waste plant and the
 * vault); the seventh is the vessel every sealed heating system has.
 *
 * Read order is the one the other families use: the setting (`tank_type`), then
 * the icon the removed "Tank type" row wrote, then the class the hub filed the
 * device under, then the name. The class default `water_full` stays out of the
 * icon map, so a tank that has never been picked is drawn by what it is.
 */
export type TankKind = 'ibc' | 'cistern' | 'horizontal' | 'buffer' | 'treatment' | 'septic' | 'expansion';

/** The values `settings.tank_type` accepts — the drawing ids themselves. */
export const TANK_KIND_VALUES: readonly TankKind[] = [
  'ibc',
  'cistern',
  'horizontal',
  'buffer',
  'treatment',
  'septic',
  'expansion',
];

/** The icons the legacy "Tank type" row wrote, as the drawing each means. */
const TANK_ICON_KINDS: Record<string, TankKind> = {
  package_2: 'ibc',
  propane_tank: 'cistern',
  water_bottle_large: 'horizontal',
  water_heater: 'buffer',
  water_damage: 'treatment',
  foundation: 'septic',
  compress: 'expansion',
};

/** The class ids that ARE a tank — the cistern and the waste plant families. */
export const TANK_CLASS_IDS: readonly string[] = [...WATER_TANK_CLASS_IDS, ...WASTE_TANK_CLASS_IDS];

/** Whether the device belongs to one of the hub's tank classes. */
export function isTankDevice(device: PumpDeviceLike & { classId?: string | null }): boolean {
  return [device.deviceType, device.controlType, device.deviceControl, device.classId].some((value) =>
    TANK_CLASS_IDS.includes(
      String(value ?? '')
        .trim()
        .toLowerCase(),
    ),
  );
}

/** The two waste classes the hub spells, as the drawing each means. */
function tankKindFromClass(id: string): TankKind | null {
  if (id === 'septic_tank') return 'septic';
  if (id === 'waste_tank' || id === 'treatment_plant') return 'treatment';
  return null;
}

/** The words a tank names its own kind with, most specific first. */
function tankKindFromName(text: string): TankKind | null {
  if (!text) return null;
  if (/ibc|tote\b/.test(text)) return 'ibc';
  if (/horizontal|lying|laying|le[zž]e[cč]/.test(text)) return 'horizontal';
  if (/buffer|thermal[_ ]?store|thermal|akumulacij|hranilnik|bojler|hot[_ ]?water[_ ]?(cylinder|tank|store)/.test(text))
    return 'buffer';
  if (/septic|greznica|septi[cč]n/.test(text)) return 'septic';
  if (/treatment|biological|biolo[sš]k|[cč]istiln|waste[_ ]?water|wastewater/.test(text)) return 'treatment';
  if (/expansion|raztezn|membrane[_ ]?vessel/.test(text)) return 'expansion';
  if (/cistern|rainwater|de[zž]evnic|reservoir|rezervoar|storage|water[_ ]?tank|\btank\b/.test(text)) return 'cistern';
  return null;
}

/** Pick the tank illustration: the setting, the icon, the class, then the name. */
export function tankKindOf(
  device: {
    icon?: string | null;
    deviceName?: string | null;
    shortName?: string | null;
    /** `settings` — the hub's `tank_type` outranks every loose hint. */
    settings?: unknown;
  } & PumpDeviceLike,
): TankKind {
  const fromSetting = variantFromSetting(device.settings, 'tank_type', TANK_KIND_VALUES);
  if (fromSetting) return fromSetting as TankKind;

  const icon = (device.icon ?? '').trim().toLowerCase();
  const fromIcon = TANK_ICON_KINDS[icon];
  if (fromIcon) return fromIcon;

  const classId = String(device.deviceType ?? '')
    .trim()
    .toLowerCase();
  const fromClass = tankKindFromClass(classId);
  if (fromClass) return fromClass;

  const haystack = [device.deviceName, device.shortName, device.deviceType, device.controlType, device.deviceControl]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return tankKindFromName(haystack) ?? 'cistern';
}

/**
 * Which heat-pump drawing the tile shows (see `heat-pump/HeatPumpAnimation.vue`).
 *
 * The hub's `heat_pump` class is a *climate* device — an on/off, a room
 * temperature, a target and an operation mode — and it used to render as a plain
 * thermostat dial. The drawing says where the pump takes its heat from:
 * `air_water` the outdoor unit with its fan and coil, `ground` the borehole's
 * brine loops, `water` a well loop, `air_air` an indoor louvre, `exhaust` an
 * extract duct, `dhw` a cylinder, `pool` a pool heater, and `hybrid` the pump
 * beside the boiler that backs it up.
 *
 * Read order is the one every family uses: the `heat_pump_type` setting, then the
 * icon and the name, then `air_water` — the ordinary install.
 */
export type HeatPumpKind = 'air_water' | 'ground' | 'water' | 'air_air' | 'exhaust' | 'dhw' | 'pool' | 'hybrid';

/** The values `settings.heat_pump_type` accepts — the drawing ids themselves. */
export const HEAT_PUMP_KIND_VALUES: readonly HeatPumpKind[] = [
  'air_water',
  'ground',
  'water',
  'air_air',
  'exhaust',
  'dhw',
  'pool',
  'hybrid',
];

/** The picker's own glyphs, plus the icons a driver or an owner may have attached. */
const HEAT_PUMP_ICON_KINDS: Record<string, HeatPumpKind> = {
  ac_unit: 'air_water',
  heat_pump: 'air_water',
  hvac: 'air_water',
  mode_fan: 'air_water',
  landscape: 'ground',
  terrain: 'ground',
  water: 'water',
  air: 'air_air',
  wind_power: 'exhaust',
  water_heater: 'dhw',
  pool: 'pool',
  propane_tank: 'hybrid',
};

/** The words a heat pump names its own source with, most specific first. */
function heatPumpKindFromName(text: string): HeatPumpKind | null {
  if (!text) return null;
  if (/bazen|pool/.test(text)) return 'pool';
  if (/sanitarn|\bdhw\b|bojler|hot[_ ]?water|topla voda/.test(text)) return 'dhw';
  if (/hibrid|hybrid/.test(text)) return 'hybrid';
  if (/prezra[cč]evanj|exhaust|ventilacij|heat[_ ]?recovery/.test(text)) return 'exhaust';
  if (/zemlja|ground|brine|geosonda|slanica|borehole|vrtina/.test(text)) return 'ground';
  if (/voda[-_ ]?voda|water[-_ ]?source|water-to-water/.test(text)) return 'water';
  if (/zrak[-_ ]?zrak|air[-_ ]?to[-_ ]?air|multi[_ ]?split|klima/.test(text)) return 'air_air';
  return null;
}

/** Pick the heat-pump illustration: the setting, the icon, the name, then air-to-water. */
export function heatPumpKindOf(
  device: {
    icon?: string | null;
    deviceName?: string | null;
    shortName?: string | null;
    /** `settings` — the hub's `heat_pump_type` outranks every loose hint. */
    settings?: unknown;
  } & PumpDeviceLike,
): HeatPumpKind {
  const fromSetting = variantFromSetting(device.settings, 'heat_pump_type', HEAT_PUMP_KIND_VALUES);
  if (fromSetting) return fromSetting as HeatPumpKind;

  const icon = (device.icon ?? '').trim().toLowerCase();
  const fromIcon = HEAT_PUMP_ICON_KINDS[icon];
  if (fromIcon) return fromIcon;

  const haystack = [device.deviceName, device.shortName, device.deviceType, device.controlType, device.deviceControl]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return heatPumpKindFromName(haystack) ?? 'air_water';
}

/**
 * Whether the device is the hub's own `heat_pump` class — the art and the widget
 * are for the plant, never for the pump that circulates its water (that is the
 * `pump` class, drawn as a circulator).
 */
export function isHeatPumpDevice(device: PumpDeviceLike): boolean {
  return [device.deviceType, device.controlType, device.deviceControl].some(
    (value) =>
      String(value ?? '')
        .trim()
        .toLowerCase() === 'heat_pump',
  );
}

/**
 * Which lock drawing the tile shows (see `lock/LockAnimation.vue`).
 *
 * The hub's `lock` class carries one boolean — locked — and its icon said only
 * "a lock"; the drawing says which lock it is: `cylinder` the rotary knob with
 * its red/green state dot, `strike` a close-up of the door's edge, its lever and
 * the strike cavity behind it. Two only (owner, 2026-10-26: "strike: similar to
 * door, just closeup of the handle and open/closed cavity.. remove magnetic..
 * rotary cylinder: just rotary knob, with indicator").
 *
 * Read order is the one every family uses: the `lock_type` setting, then the icon
 * and the name, then `cylinder` — the ordinary Slovenian door lock.
 */
export type LockKind = 'cylinder' | 'strike';

/** The values `settings.lock_type` accepts — the drawing ids themselves. */
export const LOCK_KIND_VALUES: readonly LockKind[] = ['cylinder', 'strike'];

/**
 * The picker's own glyphs, plus the icons a driver or an owner may have attached.
 * Only the two drawn locks exist, so every other hardware word folds into the
 * cylinder it still is at heart; the electric-lock glyphs land on the strike.
 */
const LOCK_ICON_KINDS: Record<string, LockKind> = {
  lock: 'cylinder',
  lock_open: 'cylinder',
  vpn_key: 'cylinder',
  key: 'cylinder',
  door_front: 'cylinder',
  fingerprint: 'cylinder',
  pin: 'cylinder',
  enhanced_encryption: 'cylinder',
  sensor_door: 'cylinder',
  sensor_window: 'cylinder',
  garage: 'cylinder',
  sensor_occupied: 'strike',
  bolt: 'strike',
};

/** The words a lock names its own kind with, most specific first. */
function lockKindFromName(text: string): LockKind | null {
  if (!text) return null;
  // The electric openings, magnetic ones included: the strike is how a door
  // releases, and a magnet is the same idea with plates instead of a jaw.
  if (/magnet|strike|odpiralo|buzzer|door opener/.test(text)) return 'strike';
  // Everything else that is a lock at all is drawn as the rotary cylinder: a
  // deadbolt, a mortise body, a keypad panel and a padlock are the same lock in
  // this vocabulary.
  if (
    /padlock|obesna|kolesarsk|shackle|bike lock|smart|keypad|tipkovnic|fingerprint|prstn|biometr|koda\b|nuki|yale|august|deadbolt|rim lock|mrtvi|night latch|no[cč]ni|barrel|zapah|zasun|sliding bolt|slide bolt|mortise|vgradna|lever|ro[cč]ica|cylind|cilinder|vlo[zž]ek|profile|klju[cč]avnic|lock/.test(
      text,
    )
  ) {
    return 'cylinder';
  }
  return null;
}

/** Pick the lock illustration: the setting, the icon, the name, then a cylinder. */
export function lockKindOf(
  device: {
    icon?: string | null;
    deviceName?: string | null;
    shortName?: string | null;
    /** `settings` — the hub's `lock_type` outranks every loose hint. */
    settings?: unknown;
  } & PumpDeviceLike,
): LockKind {
  const fromSetting = variantFromSetting(device.settings, 'lock_type', LOCK_KIND_VALUES);
  if (fromSetting) return fromSetting as LockKind;

  const icon = (device.icon ?? '').trim().toLowerCase();
  const fromIcon = LOCK_ICON_KINDS[icon];
  if (fromIcon) return fromIcon;

  const haystack = [device.deviceName, device.shortName, device.deviceType, device.controlType, device.deviceControl]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return lockKindFromName(haystack) ?? 'cylinder';
}

/**
 * Whether the device editor should offer the reading-type row — the picker that
 * sets a tile's icon, unit and name from a measurement (temperature, humidity,
 * power…).
 *
 * Keyed on what the device *reports*, never on "it has values": the row used to
 * render for anything with a reading, which is every device in the house, and
 * so the sensor icons appeared on sockets, covers, thermostats and lights.
 */
export function reportsReadings(
  controlType: string | null | undefined,
  deviceType: string | null | undefined,
): boolean {
  const ctrl = (controlType ?? '').toLowerCase();
  const dt = (deviceType ?? '').toLowerCase();
  if (ctrl.includes('binary_sensor')) return false;
  return (
    ctrl.includes('sensor') ||
    ctrl.includes('meter') ||
    /battery|inverter|meter|weather|station|co2|air|\bsensor\b|notification/.test(dt)
  );
}

/**
 * Whether the device editor should offer the appliance row — a plug, a socket,
 * a dimmable plug or a relay output, i.e. something with a load hanging off it.
 */
export function isPlugLike(controlType: string | null | undefined, deviceType: string | null | undefined): boolean {
  const ctrl = (controlType ?? '').toLowerCase();
  const dt = (deviceType ?? '').toLowerCase();
  return dt === 'socket' || dt === 'dimmable_switch' || dt === 'binary_switch' || ctrl === 'socket';
}

/**
 * The appliance words, most specific first. `pump` sits late on purpose: a
 * "Heat Pump" is the plant, not a wet-rotor pump on a socket, and the heat-pump
 * class never routes here anyway.
 */
function applianceKind(text: string): SwitchKind | null {
  if (!text) return null;
  if (/\bev\b|ev_?charg|charger|polnilnic|car charger|wallbox/.test(text)) return 'ev';
  if (/coffee|espresso|kavni|kava\b/.test(text)) return 'coffee';
  if (/wash(ing)?[_ ]?machine|washer|pralni|pranje/.test(text)) return 'washer';
  if (/dryer|tumble|su[sš]ilni/.test(text)) return 'dryer';
  if (/dishwash|pomivalni/.test(text)) return 'dishwasher';
  if (/fridge|freezer|refriger|hladilnik|skrinja/.test(text)) return 'fridge';
  if (/oven|stove|cooker|pe[cč]ica|[sš]tedilnik/.test(text)) return 'oven';
  if (/kettle|boiler|water heater|grelec|grelnik/.test(text)) return 'kettle';
  if (/vacuum|hoover|sesal|robot vacuum/.test(text)) return 'vacuum';
  if (/mower|kosilnic|lawn/.test(text)) return 'mower';
  if (/siren|alarm horn|sirena/.test(text)) return 'siren';
  if (/doorbell|bell\b|zvonec|zvon/.test(text)) return 'bell';
  // `ventil` alone is a valve, `ventilator` a fan — the word has to stand on its own.
  if (/valve|\bventil\b|zaklop|faucet|water tap/.test(text)) return 'valve';
  // A heat pump is the plant, not a wet-rotor pump on a plug.
  if (/pump|črpalk|crpalk/.test(text) && !/heat[_ ]?pump|toplotn/.test(text)) return 'pump';
  if (/dimmer|dimmable|lamp plug|svetilka/.test(text)) return 'dimmer';
  if (/socket|outlet|plug|vti[cč]nica/.test(text)) return 'socket';
  return null;
}
